import { LockFilled } from '@ant-design/icons';
import { Modal } from 'antd';
import type { TFunction } from 'i18next';
import { getI18n } from 'react-i18next';

import { extractApiError } from '@/originator/statusUtils';

import { message } from './message';

type LockState = {
  holderName?: string | null;
  expiresAt?: string | null;
};

type LockConflictBody = {
  code?: string;
  lockState?: LockState;
};

/**
 * Routes API errors to the right surface:
 *   - 409 LOCK_HELD_BY_OTHER → a sticky <code>Modal.warning</code> that the user must
 *     close manually. Lock collisions are important — if Bob took over while Alice was
 *     mid-edit, an auto-dismissing toast disappears before she can read it.
 *   - everything else → the usual <code>message.error</code> toast.
 *
 * Callers pass the i18n <code>t</code> and an optional fallback (used when the BE
 * didn't supply a human-readable <code>message</code>).
 */
export function handleApiError(err: unknown, t: TFunction, fallback?: string): void {
  const apiErr = extractApiError(err);
  const body = apiErr.body as LockConflictBody | undefined;
  if (apiErr.status === 409 && body?.code === 'LOCK_HELD_BY_OTHER') {
    showLockConflictModal(body.lockState, t);
    return;
  }
  message.error(apiErr.message ?? fallback ?? t('common.unknownError'));
}

function showLockConflictModal(lockState: LockState | undefined, t: TFunction): void {
  const holderName = lockState?.holderName ?? t('workflow.lock.anotherUser');
  const expiresAtIso = lockState?.expiresAt;
  // Use the active i18n language so the modal's date format matches the in-page banner
  // (which already passes i18n.language to toLocaleString). Without this, a French user
  // would see an en-US date in the modal and an fr-CA date in the banner on the same screen.
  const locale = getI18n().language;
  const expiresAt = expiresAtIso ? new Date(expiresAtIso).toLocaleString(locale) : null;

  Modal.warning({
    icon: (
      <span
        // Filled lock badge — same visual language as the in-page banner so the
        // modal feels like the "more emphatic" version of the same status.
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: '#f59e0b',
          color: '#fff',
          fontSize: 20,
          marginRight: 12,
          boxShadow: '0 1px 2px rgba(180, 83, 9, 0.25)',
        }}
      >
        <LockFilled />
      </span>
    ),
    title: (
      <span style={{ fontWeight: 700, color: '#7c2d12' }}>
        {t('workflow.lock.bannerTitle', { name: holderName })}
      </span>
    ),
    content: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, color: '#92400e' }}>
        {expiresAt && <div>{t('workflow.lock.bannerExpiry', { time: expiresAt })}</div>}
        <div>{t('workflow.lock.bannerCantEdit')}</div>
      </div>
    ),
    okText: t('common.gotIt'),
    // Sticky: only the explicit OK button dismisses it. Outside clicks, ESC, and the
    // top-right close X are all disabled so the user can read the message in full.
    closable: false,
    keyboard: false,
    maskClosable: false,
    width: 480,
  });
}
